import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleXmark } from "@fortawesome/free-solid-svg-icons";
import "./reserve.css";
import { useContext, useState } from "react";
import { SearchContext } from "../../context/SearchContext";
import { BookingContext } from "../../context/BookingContext";
import useAuth from "../../hooks/useAuth";
import { useNavigate } from "react-router-dom";
import { bookings, payments } from "../../api/apiService";
import { getErrorMessage } from "../../api/errorUtils";
import { format } from "date-fns";
import BookingDates from "../datePicker/BookingDates";
import { getBookingDateRange } from "../../utils/bookingDates";

const Reserve = ({ setOpen, hotelId, hotel, rooms = [] }) => {
  const [selected, setSelected] = useState(null);
  const [bookingError, setBookingError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const { dates, options } = useContext(SearchContext);
  const { dispatch: bookingDispatch } = useContext(BookingContext);
  const { user } = useAuth();
  const navigate = useNavigate();

  const getDatesInRange = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const current = new Date(start.getTime());
    const result = [];
    while (current < end) {
      result.push(format(current, "yyyy-MM-dd"));
      current.setDate(current.getDate() + 1);
    }
    return result;
  };

  const { checkInDate, checkOutDate, nights } = getBookingDateRange(dates);

  const isAvailable = (roomNumber) => {
    if (!dates?.[0]) return true;
    const rangeDates = getDatesInRange(dates[0].startDate, dates[0].endDate);
    return !roomNumber.unavailableDates?.some((d) =>
      rangeDates.includes(format(new Date(d), "yyyy-MM-dd"))
    );
  };

  const handleSelect = (room, roomNumber) => {
    setSelected({ room, roomNumber });
    setBookingError(null);
  };

  const loadRazorpayScript = () =>
    new Promise((resolve, reject) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }

      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => reject(new Error("Razorpay SDK failed to load."));
      document.body.appendChild(script);
    });

  const openRazorpayCheckout = async ({ amount, currency, orderId, keyId, bookingId }) => {
    await loadRazorpayScript();

    return new Promise((resolve, reject) => {
      if (!window.Razorpay) {
        reject(new Error("Razorpay SDK is unavailable."));
        return;
      }

      const options = {
        key: keyId,
        amount,
        currency,
        order_id: orderId,
        name: "StayCation",
        description: "Hotel booking payment",
        prefill: {
          name: user?.name || user?.username || "",
          email: user?.email || "",
          contact: user?.phone || "",
        },
        handler: async function (response) {
          try {
            const confirmRes = await payments.confirm({
              bookingId,
              razorpayPaymentId: response.razorpay_payment_id,
              razorpayOrderId: response.razorpay_order_id,
              razorpaySignature: response.razorpay_signature,
            });

            if (!confirmRes?.data?.success) {
              throw new Error("Payment confirmation failed.");
            }

            resolve(confirmRes.data.booking || null);
          } catch (error) {
            reject(error);
          }
        },
        modal: {
          ondismiss: () => {
            reject(new Error("Payment was cancelled by the user."));
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    });
  };

  const handleBook = async () => {
    if (!selected) {
      setBookingError("Please select an available room number.");
      return;
    }
    if (!checkInDate || !checkOutDate) {
      setBookingError("Please select check-in and check-out dates from search.");
      return;
    }

    const totalPrice = selected.room.price * nights;
    const payload = {
      hotelId,
      roomId: selected.room._id,
      roomNumberId: selected.roomNumber._id,
      checkInDate,
      checkOutDate,
      totalPrice,
      guests: options?.adult || 1,
    };

    setSubmitting(true);
    setBookingError(null);

    let createdBooking = null;
    try {
      const res = await bookings.create(payload);
      createdBooking = res.data.booking;
      if (!createdBooking?._id) {
        throw new Error("Booking creation did not return a valid booking.");
      }

      const paymentRes = await payments.create({
        amount: totalPrice,
        currency: "INR",
        bookingId: createdBooking._id,
      });

      const { orderId, amount, currency, keyId } = paymentRes.data;
      if (!orderId || !amount || !currency || !keyId) {
        throw new Error("Invalid payment order response from server.");
      }

      const confirmedBooking = await openRazorpayCheckout({
        amount,
        currency,
        orderId,
        keyId,
        bookingId: createdBooking._id,
      });

      bookingDispatch({
        type: "SET_BOOKING_RESULT",
        payload: confirmedBooking || createdBooking,
      });
      bookingDispatch({
        type: "SET_SELECTION",
        payload: { ...payload, lastBooking: confirmedBooking || createdBooking },
      });

      setOpen(false);
      navigate("/success");
    } catch (err) {
      const status = err?.response?.status;
      if (createdBooking?._id) {
        bookings.cancel(createdBooking._id).catch(() => null);
      }
      if (status === 409) {
        setBookingError("Selected dates are no longer available. Please pick another room.");
      } else {
        setBookingError(getErrorMessage(err, err.message || "Booking or payment failed. Please try again."));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="reserve">
      <div className="rContainer">
        <FontAwesomeIcon
          icon={faCircleXmark}
          className="rClose"
          onClick={() => setOpen(false)}
        />
        <span>Select a room at {hotel?.name}:</span>
        <BookingDates compact />
        {rooms.map((item) => (
          <div className="rItem" key={item._id}>
            <div className="rItemInfo">
              <div className="rTitle">{item.title}</div>
              <div className="rDesc">{item.desc}</div>
              <div className="rMax">
                Max people: <b>{item.maxPeople}</b>
              </div>
              <div className="rPrice">${item.price}/night</div>
            </div>
            <div className="rSelectRooms">
              {item.roomNumbers?.map((roomNumber) => {
                const available = isAvailable(roomNumber);
                const isSelected =
                  selected?.roomNumber?._id === roomNumber._id;
                return (
                  <div className="room" key={roomNumber._id}>
                    <label>
                      Room {roomNumber.number}
                      {!available && " (unavailable)"}
                    </label>
                    <input
                      type="radio"
                      name="roomNumber"
                      disabled={!available}
                      checked={isSelected}
                      onChange={() => handleSelect(item, roomNumber)}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        ))}
        {bookingError && <div className="reserve-error">{bookingError}</div>}
        <button
          type="button"
          onClick={handleBook}
          className="rButton"
          disabled={submitting}
        >
          {submitting ? "Processing payment..." : "Pay and Confirm Booking"}
        </button>
      </div>
    </div>
  );
};

export default Reserve;
